import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../lib/utils'

export const buttonVariants = cva(
  [
    'ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center gap-2 rounded-lg text-center text-sm font-medium',
    'touch-manipulation transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-150 ease-out',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
    'motion-safe:active:scale-[0.98] motion-reduce:active:scale-100',
  ],
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 shadow-sm shadow-black/[0.06] dark:shadow-black/35',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80 shadow-sm shadow-black/[0.06] dark:shadow-black/30',
        outline:
          'border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent/90 dark:active:bg-accent/80 border shadow-sm shadow-black/[0.03] dark:shadow-black/20',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70 shadow-sm shadow-black/[0.05] dark:shadow-black/25',
        ghost: 'hover:bg-accent hover:text-accent-foreground active:bg-accent/80 dark:active:bg-accent/60',
        link: 'text-primary rounded-md underline-offset-4 shadow-none hover:underline active:opacity-80 motion-safe:active:scale-100',
        icon: 'text-secondary-foreground focus-visible:ring-ring dark:focus-visible:ring-ring aspect-square shrink-0 rounded-full border border-white/50 bg-white/25 shadow-lg ring-1 shadow-black/[0.08] ring-black/[0.04] backdrop-blur-2xl backdrop-saturate-150 transition-[transform,background-color,border-color,box-shadow] duration-150 ease-out not-focus-visible:ring-inset hover:bg-white/40 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-0 motion-safe:active:scale-[0.97] dark:border-white/[0.14] dark:bg-slate-950/35 dark:shadow-black/40 dark:ring-white/[0.06] dark:backdrop-blur-2xl dark:hover:bg-slate-950/50 dark:hover:ring-white/[0.1]',
        iconDestructive:
          'border-destructive/40 bg-destructive/20 text-destructive-foreground ring-destructive/15 hover:border-destructive/55 hover:bg-destructive/35 dark:border-destructive/45 dark:bg-destructive/35 dark:ring-destructive/10 dark:hover:bg-destructive/50 dark:hover:ring-destructive/20 focus-visible:ring-ring aspect-square shrink-0 rounded-full border shadow-lg ring-1 shadow-black/[0.06] backdrop-blur-2xl backdrop-saturate-150 transition-[transform,background-color,border-color,box-shadow] duration-150 ease-out not-focus-visible:ring-inset hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-0 motion-safe:active:scale-[0.97] dark:shadow-black/40',
      },
      size: {
        default: 'h-10 min-h-10 px-4 py-2',
        sm: 'h-9 min-h-9 rounded-md px-3',
        lg: 'h-11 min-h-11 rounded-lg px-8',
        icon: 'h-10 min-h-10 w-10 min-w-10',
        iconLg: 'h-12 min-h-12 w-12 min-w-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = ButtonPrimitive.Props & VariantProps<typeof buttonVariants>

export const Button = ({ className, variant = 'default', size = 'default', ...props }: ButtonProps) => (
  <ButtonPrimitive className={cn(buttonVariants({ variant, size, className }))} data-slot="button" {...props} />
)
